import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

interface DataPoint {
  date: string;
  visitors: number;
  pageviews: number;
}

interface AnalyticsChartProps {
  data: DataPoint[];
  period: '7d' | '30d' | '90d';
}

function formatDate(dateStr: string, period: string): string {
  const date = new Date(dateStr);
  if (period === '7d') {
    return date.toLocaleDateString('en-US', { weekday: 'short', hour: 'numeric' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}> = ({ active, payload, label }) => {
  if (!active || !payload || !label) return null;

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-3 shadow-xl">
      <p className="mb-2 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
        {new Date(label).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
      </p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-[13px] text-primary">
            {entry.name}: <strong>{entry.value.toLocaleString()}</strong>
          </span>
        </div>
      ))}
    </div>
  );
};

const AnalyticsChart: React.FC<AnalyticsChartProps> = ({ data, period }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-[15px] text-muted">
        No data available for this period
      </div>
    );
  }

  const formattedData = data.map((d) => ({
    ...d,
    label: formatDate(d.date, period),
  }));

  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={formattedData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border, #e4e4e7)" />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 12, fill: 'var(--color-muted, #71717a)' }}
          tickLine={false}
          axisLine={{ stroke: 'var(--color-border, #e4e4e7)' }}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={{ fontSize: 12, fill: 'var(--color-muted, #71717a)' }}
          tickLine={false}
          axisLine={false}
          width={40}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }}
        />
        <Line
          type="monotone"
          dataKey="visitors"
          name="Visitors"
          stroke="#1e2228"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4, fill: '#1e2228' }}
        />
        <Line
          type="monotone"
          dataKey="pageviews"
          name="Page Views"
          stroke="#71717a"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4, fill: '#71717a' }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default AnalyticsChart;
